import React from 'react'
import { useCssHandles } from '../hooks/useCssHandles'
import { Layout } from '../shared/components'

const CSS_HANDLES = [
  'termsPage',
  'termsContainer',
  'termsHeader',
  'termsTitle',
  'termsSubtitle',
  'termsContent',
  'termsSection',
  'termsSectionTitle',
  'termsSectionContent',
  'termsList',
  'termsListItem',
  'termsHighlight',
  'termsContact'
] as const

const TermsOfUse: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)

  return (
    <Layout>
      <div className={`${handles.termsPage} min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-16`}>
        <div className={`${handles.termsContainer} max-w-4xl mx-auto px-6`}>
          
          {/* Header */}
          <div className={`${handles.termsHeader} text-center mb-12`}>
            <h1 className={`${handles.termsTitle} text-4xl md:text-5xl font-bold text-gray-800 mb-4`}>
              Termos de Uso
            </h1>
            <p className={`${handles.termsSubtitle} text-lg text-gray-600 max-w-2xl mx-auto`}>
              Conheça os termos e condições que regem o uso da nossa plataforma de baterias automotivas
            </p>
          </div>

          {/* Content */}
          <div className={`${handles.termsContent} bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8`}>
            
            {/* Seção 1 - Aceitação dos Termos */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">1</span>
                Aceitação dos Termos
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Ao acessar e utilizar o site da <span className={`${handles.termsHighlight} font-semibold text-blue-600`}>Baterpower Baterias</span>, 
                  você concorda em cumprir e estar vinculado aos seguintes termos e condições de uso.
                </p>
                <p>
                  Estes termos se aplicam a todos os visitantes, usuários e outras pessoas que acessam ou utilizam o serviço.
                </p>
              </div>
            </section>

            {/* Seção 2 - Descrição do Serviço */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">2</span>
                Descrição do Serviço
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  A Baterpower Baterias é uma plataforma de e-commerce especializada na venda de baterias automotivas, 
                  oferecendo:
                </p>
                <ul className={`${handles.termsList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.termsListItem}`}>Catálogo completo de baterias automotivas</li>
                  <li className={`${handles.termsListItem}`}>Sistema de busca por compatibilidade de veículos</li>
                  <li className={`${handles.termsListItem}`}>Processamento seguro de pagamentos</li>
                  <li className={`${handles.termsListItem}`}>Entrega em todo o território nacional</li>
                  <li className={`${handles.termsListItem}`}>Suporte técnico especializado</li>
                </ul>
              </div>
            </section>

            {/* Seção 3 - Uso Aceitável */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">3</span>
                Uso Aceitável
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>Você concorda em não utilizar nosso serviço para:</p>
                <ul className={`${handles.termsList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.termsListItem}`}>Qualquer propósito ilegal ou não autorizado</li>
                  <li className={`${handles.termsListItem}`}>Transmitir ou obter dados que violem leis ou regulamentos</li>
                  <li className={`${handles.termsListItem}`}>Interferir com o funcionamento do serviço</li>
                  <li className={`${handles.termsListItem}`}>Tentar obter acesso não autorizado a sistemas</li>
                  <li className={`${handles.termsListItem}`}>Usar o serviço de forma que possa danificar, desabilitar ou sobrecarregar nossos servidores</li>
                </ul>
              </div>
            </section>

            {/* Seção 4 - Contas de Usuário */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">4</span>
                Contas de Usuário
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Ao criar uma conta em nosso serviço, você é responsável por:
                </p>
                <ul className={`${handles.termsList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.termsListItem}`}>Manter a confidencialidade de sua senha</li>
                  <li className={`${handles.termsListItem}`}>Fornecer informações verdadeiras e atualizadas</li>
                  <li className={`${handles.termsListItem}`}>Notificar-nos imediatamente sobre qualquer uso não autorizado</li>
                  <li className={`${handles.termsListItem}`}>Ser responsável por todas as atividades em sua conta</li>
                </ul>
              </div>
            </section>

            {/* Seção 5 - Propriedade Intelectual */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">5</span>
                Propriedade Intelectual
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  O serviço e seu conteúdo original, funcionalidades e funcionalidades são e permanecerão 
                  propriedade exclusiva da Baterpower Baterias e seus licenciadores.
                </p>
                <p>
                  Nossa marca, logotipos e outros materiais promocionais são marcas comerciais registradas 
                  e não podem ser reproduzidos sem autorização expressa.
                </p>
              </div>
            </section>

            {/* Seção 6 - Limitação de Responsabilidade */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">6</span>
                Limitação de Responsabilidade
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Em nenhuma circunstância a Baterpower Baterias será responsável por danos indiretos, 
                  incidentais, especiais, consequenciais ou punitivos, incluindo, sem limitação, 
                  perda de lucros, dados ou uso, decorrentes do uso do serviço.
                </p>
              </div>
            </section>

            {/* Seção 7 - Modificações */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">7</span>
                Modificações dos Termos
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Reservamo-nos o direito de modificar ou substituir estes termos a qualquer momento. 
                  Se uma revisão for material, tentaremos fornecer pelo menos 30 dias de aviso antes 
                  que os novos termos entrem em vigor.
                </p>
                <p>
                  O uso continuado do serviço após essas alterações constitui sua aceitação dos novos termos.
                </p>
              </div>
            </section>

            {/* Seção 8 - Contato */}
            <section className={`${handles.termsSection}`}>
              <h2 className={`${handles.termsSectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">8</span>
                Contato
              </h2>
              <div className={`${handles.termsSectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Se você tiver alguma dúvida sobre estes Termos de Uso, entre em contato conosco:
                </p>
                <div className={`${handles.termsContact} bg-blue-50 rounded-xl p-6 mt-6`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">📧 E-mail</h4>
                      <p className="text-blue-700">contato@baterpowerbaterias.com.br</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">📞 Telefone</h4>
                      <p className="text-blue-700">(19) 3255-2356</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">📍 Endereço</h4>
                      <p className="text-blue-700">
                        Av. José de Sousa Campos, 2086<br />
                        Nova Campinas, Campinas - SP
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">🕒 Horário</h4>
                      <p className="text-blue-700">
                        Seg-Sex: 8h às 18h<br />
                        Sáb: 8h às 14h
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Data de Atualização */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-sm text-gray-500 text-center">
                <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default TermsOfUse
